<template>
    <NewAgreementDialog v-model="showNewAgreementDialog" :horseName="horse.name" @search="search" v-model:renew="renew" v-model:rowData="rowData"
                        :lastCoveringAgreementType="lastCoveringAgreementType" />
    <NewCertificateDialog v-model="showNewCoveringDialog" :horseName="horse.name" @search="search" @refreshHorse="$emit('refreshHorse')" />
    <AddResultDialog v-model="showAddResultDialog" :horseName="horse.name" @search="search" v-model:rowData="rowData" />
    <div class="covering-edit-view flex flex-column w-full gap-5 h-full justify-content-start">
        <div class="flex justify-content-between align-items-center">
            <h2 class="covering-edit-header">{{ $t('message.horseEdit.covering.header') }} ({{ horse.name }})</h2>
            <div v-if="checkIfYoungerThen()" class="flex align-items-center gap-2">
                <i class="pi pi-exclamation-triangle attention-icon" />
                <span v-if="horse.showStallionAgreements" style="color: red;">{{ $t('message.horseEdit.covering.attentionStalion') }}</span>
                <span v-if="horse.showCoveringCertificates" style="color: red;">{{ $t('message.horseEdit.covering.attentionBroodmare') }}</span>
            </div>
        </div>
        <div v-if="horse.showStallionAgreements" class="section-table w-full section-table-stallion">
            <div class="flex justify-content-between align-items-center">
                <h3 class="covering-table-header">{{ $t('message.horseEdit.covering.tableHeaderStallion') }}</h3>
                <CustomButton :label="$t('message.horseEdit.covering.newAgreement')" @click="() => showNewAgreementDialog = true"/>
            </div>
            <DataTable :value="coveringAgreements" dataKey="id" lazy :loading="loading" class="covering-agreement-table"
                       paginator v-model:rows="searchCriteria.page.limit" v-model:first="searchCriteria.page.offset"
                       :totalRecords="totalRecordsStallion" @page="onPage" @sort="onPage"
                       :rowsPerPageOptions="[5,10,20,50]" scrollHeight="calc(100vh - 300px)" v-model:editingRows="editingRows" editMode="row" @row-edit-save="onRowEditSave">
                <Column field="type" :header="$t('message.horseEdit.covering.type')" class="w-2">
                    <template #editor="{data,field}">
                        <CustomSelectOneAgreementType v-model="data[field]" name="type" />
                    </template>
                </Column>
                <Column field="year" :header="$t('message.horseEdit.covering.season')" class="w-1"></Column>
                <Column field="location" :header="$t('message.horseEdit.covering.location')" class="w-1"></Column>
                <Column :header="$t('message.horseEdit.covering.certificateNumber')" class="w-1">
                    <template #body="data">
                        <div>
                            {{ data.data.agreementNumberMin }} - {{ data.data.agreementNumberMax }}
                        </div>
                    </template>
                </Column>
                <Column field="notes" :header="$t('message.horseEdit.covering.notes')" class="w-1">
                    <template #body="data">
                        <div v-if="data.data?.notes?.length > 80" v-tooltip.top="{value: data.data.notes,class: 'tooltip-style'}">
                            {{ computedNotes(data.data.notes) }}
                        </div>
                        <div v-else>
                            {{ data.data.notes }}
                        </div>
                    </template>
                </Column>
                <Column v-if="editMode" style="width: 4.1666%; text-align: center;">
                    <template #editor="{data,editorSaveCallback,editorCancelCallback}">
                        <div class="flex flex-column justify-content-center align-items-center gap-3">
                            <CustomIconButton icon="pi pi-check" onlyIcon @click="() => { editorSaveCallback(); editMode = false }" />
                            <CustomIconButton icon="pi pi-times" onlyIcon @click="() => { editorCancelCallback(); editMode = false }" />
                        </div>
                    </template>
                </Column>
                <Column v-else style="width: 4.1666%; text-align: center;">
                    <template #body="{data,editorInitCallback}">
                        <div v-if="data.changeable" class="flex flex-column justify-content-center align-items-center">
                            <CustomIconButton icon="pi pi-pencil" onlyIcon @click="() => { editorInitCallback(); editMode = true; }" />
                        </div>
                    </template>
                </Column>
                <Column style="width: 4.1666%; text-align: center;">
                    <template #body="data">
                        <div v-if="data.data.renewable">
                            <CustomIconButton icon="pi pi-refresh" onlyIcon @click="renewAgreement(data.data)" title="Renew season" />
                        </div>
                    </template>
                </Column>
                <Column style="width: 4.1666%; text-align: center;">
                    <template #body="data">
                        <CustomIconButton icon="pi pi-trash" onlyIcon @click="deleteCovAgr(data.data.id)" />
                    </template>
                </Column>
            </DataTable>
        </div>
        <div v-if="horse.showCoveringCertificates" class="section-table w-full section-table-mare">
            <div class="flex justify-content-between align-items-center">
                <h3 class="covering-table-header">{{ $t('message.horseEdit.covering.tableHeaderMare') }}</h3>
                <CustomButton :label="$t('message.horseEdit.covering.newCertificate')" @click="() => showNewCoveringDialog = true"/>
            </div>
            <DataTable :value="coveringCertificates" dataKey="id" lazy :loading="loading" class="covering-certificates-table"
                       paginator v-model:rows="searchCriteria.page.limit" v-model:first="searchCriteria.page.offset"
                       :totalRecords="totalRecordsMare" @page="onPage" @sort="onPage"
                       :rowsPerPageOptions="[5,10,20,50]" scrollHeight="calc(100vh - 300px)">
                <Column field="year" :header="$t('message.horseEdit.covering.season')">
                </Column>
                <Column field="agreementNumber" :header="$t('message.horseEdit.covering.number')">
                </Column>
                <Column field="coveringTypeLabel" :header="$t('message.horseEdit.covering.coveringType')">
                </Column>
                <Column field="semenType" :header="$t('message.horseEdit.covering.semen')">
                    <template #body="data">
                        <div>
                            {{ data.data.semenTypeLabel }} {{ data.data.semenOriginLabel }}
                        </div>
                    </template>
                </Column>
                <Column field="resultType" :header="$t('message.horseEdit.covering.results')">
                    <template #body="data">
                        <div>
                            {{ data.data.resultTypeLabel }}{{ computedFoalNames(data.data) }}
                        </div>
                    </template>
                </Column>
                <Column field="firstCoveringDate" :header="$t('message.horseEdit.covering.firstCovering')"></Column>
                <Column field="lastCoveringDate" :header="$t('message.horseEdit.covering.lastCovering')"></Column>
                <Column field="externalStallionName" :header="$t('message.horseEdit.covering.externalStallion')"></Column>
                <Column field="resultType">
                    <template #body="{data}">
                        <div class="flex gap-2">
                            <CustomTextButton v-if="!data.resultType" :label="$t('message.horseEdit.covering.addResult')" @click="addResult(data)" />
                            <div class="flex gap-2">
                                <CustomTextButton v-if="data.resultType && (data.resultType === 'BORN_FOAL' || data.resultType === 'BORN_TWINS') && !data.foalOneFullName" :label="$t('message.horseEdit.covering.addFoal')" @click="addFoal(data)" />
                                <CustomTextButton v-if="data.resultType && data.resultType === 'BORN_TWINS' && !data.foalTwoFullName" :label="$t('message.horseEdit.covering.addFoal')" @click="addFoal(data)" />
                            </div>
                        </div>
                    </template>
                </Column>
            </DataTable>
        </div>
    </div>
</template>

<script>
import Tooltip from "primevue/tooltip";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import CustomInputText from "@/components/form/CustomInputText.vue";
import {
    searchCoveringAgreements,
    searchCoveringAgreementsCount,
    deleteCoveringAgreement,
    updateCoveringAgreement,
    searchCoveringCertificateForMare,
    searchCoveringCertificateForMareCount,
    getLastCoveringAgreementType,
} from "@/swagger/vue-api-client";
import CustomButton from "@/components/form/CustomButton.vue";
import NewAgreementDialog from "./dialogs/NewAgreementDialog.vue";
import CustomIconButton from "@/components/form/CustomIconButton.vue";
import CustomSelectOneAgreementType from "@/components/form/CustomSelectOneAgreementType.vue";
import NewCertificateDialog from "./dialogs/NewCertificateDialog.vue";
import CustomTextButton from "@/components/form/CustomTextButton.vue";
import AddResultDialog from "./dialogs/AddResultDialog.vue";

export default {
    name: "HorseEditMainView",

    components: {
        DataTable,
        Column,
        CustomButton,
        CustomInputText,
        NewAgreementDialog,
        NewCertificateDialog,
        CustomIconButton,
        CustomSelectOneAgreementType,
        CustomTextButton,
        AddResultDialog,
    },

    directives: {
        tooltip: Tooltip,
    },

    props: {
        horse: null,
    },

    emits: ["refreshHorse"],

    data() {
        return {
            showNewAgreementDialog: false,
            showNewCoveringDialog: false,
            showAddResultDialog: false,
            loading: false,
            coveringAgreements: [],
            coveringCertificates: [],
            totalRecordsStallion: 0,
            totalRecordsMare: 0,
            searchCriteria: {
                page: {
                    offset: 0,
                    limit: 10,
                    sortField: "season",
                    sortOrder: -1,
                },
            },
            youngerThen: false,
            editingRows: [],
            editMode: false,
            renew: false,
            rowData: null,
            lastCoveringAgreementType: null,
        };
    },

    beforeMount() {
        this.search();
    },

    methods: {
        search() {
            this.onPage(this.getFirstPage());
        },

        onPage(event) {
            this.loading = true;

            this.searchCriteria.page.offset = event.first;
            this.searchCriteria.page.limit = event.rows;
            this.searchCriteria.page.sortField = event.sortField ? event.sortField : "id";
            this.searchCriteria.page.sortOrder = event.sortOrder ? event.sortOrder : -1;

            const horseId = this.horse.id || this.$route.params.id;

            if (this.horse.showStallionAgreements) {
                searchCoveringAgreements({
                    stallionId: horseId,
                    body: this.searchCriteria,
                })
                    .then((res) => {
                    this.coveringAgreements = res.data;
                    return searchCoveringAgreementsCount({
                        stallionId: horseId,
                        body: this.searchCriteria,
})
                    .then((response) => {
                        this.totalRecordsStallion = response.data;
                        this.loading = false;
                    });
                });
                getLastCoveringAgreementType({stallionId: horseId})
                    .then((response) => {
                        this.lastCoveringAgreementType = response.data;
                    });
            }

            if (this.horse.showCoveringCertificates) {
                searchCoveringCertificateForMare({
                    mareId: horseId,
                    body: this.searchCriteria,
                })
                    .then((res) => {
                        this.coveringCertificates = res.data;
                        return searchCoveringCertificateForMareCount({
                            mareId: horseId,
                            body: this.searchCriteria,
                        })
                        .then((response) => {
                            this.totalRecordsMare = response.data;
                            this.loading = false;
                        });
                    });
            }
        },

        notifyError(error) {
            const message = error.response?.data?.message || this.$t("unknownError");
            this.$toast.add({
                severity: "error",
                summary: this.$t("message.other.error"),
                detail: message,
                life: 5000,
            });
        },

        notifyUpdate() {
            this.$toast.add({
                severity: "success",
                summary: this.$t("message.other.success"),
                detail: this.$t("message.other.dataUpdated"),
                life: 3000,
            });
        },

        getFirstPage() {
            return {
                first: this.searchCriteria.page.offset,
                rows: this.searchCriteria.page.limit,
            };
        },
        deleteCovAgr(id) {
            deleteCoveringAgreement({id})
                .then(() => {
                    this.search();
                })
                .catch((err) => {
                    this.notifyError(err);
                });
        },
        onRowEditSave(event) {
            const horseId = this.horse.id || this.$route.params.id;
            const {newData, index} = event;
            this.coveringAgreements[index] = newData;
            this.coveringAgreements[index].stallionId = horseId;
            updateCoveringAgreement({body: this.coveringAgreements[index]})
                .then(() => {
                    this.notifyUpdate();
                })
                .catch((err) => {
                    this.notifyError(err);
                });
        },
        checkIfYoungerThen() {
            let lessThen = 0;
            if (this.horse.showStallionAgreements) {
                lessThen = 24;
            }
            if (this.horse.showCoveringCertificates) {
                lessThen = 36;
            }
            const birthDate = new Date(this.horse.dateOfBirth);
            const currentDate = new Date();
            const diffInMonths = (currentDate.getFullYear() - birthDate.getFullYear()) * 12 + currentDate.getMonth() - birthDate.getMonth();
            return diffInMonths < lessThen;
        },
        computedNotes(notes) {
            let newNotes = notes;
            if (notes && notes.length > 80) {
                newNotes = notes.slice(0, 80) + "...";
            }
            return newNotes;
        },
        computedFoalNames(data) {
            if (data.resultType === "BORN_FOAL") {
                return `: ${data.foalOneFullName ? data.foalOneFullName : "?"}`;
            }
            if (data.resultType === "BORN_TWINS") {
                return `: ${data.foalOneFullName ? data.foalOneFullName : "?"}, ${data.foalTwoFullName ? data.foalTwoFullName : "?"}`;
            }
            return "";
        },
        renewAgreement(data) {
            this.rowData = data;
            this.renew = true;
            this.showNewAgreementDialog = true;
        },
        addResult(data) {
            this.rowData = data;
            this.showAddResultDialog = true;
        },
        addFoal(data) {
            this.$router.push({
                name: "newFoal",
            });
        },
    },
};
</script>

<style lang="scss">
    .covering-edit-view {
        .covering-edit-header, .covering-table-header {
            color: var(--accent-color);
            font-weight: 500;
            font-size: 35px;
            font-family: var(--decor-font-family);
        }
        .covering-edit-header {
            font-size: 35px;
        }
        .covering-table-header {
            font-size: 25px;
        }
        .attention-icon {
            font-size: 1.5rem;
        }
    }
    .tooltip-style {
        max-width: 400px;
    }
</style>
